/* eslint-disable @next/next/no-img-element */
import React, { FC } from 'react'
import Tag from '../Tag/Tag'
import AvailabilityIcon from './AvailabilityIcon'
import MoreIcon from './MoreIcon'
import SalaryIcon from './SalaryIcon'

type Seniority = 'Junior' | 'Regular' | 'Senior'

const getSeniorityLabelColor = (seniority: Seniority) => {
	switch (seniority) {
		case 'Junior': {
			return 'yellow'
		}
		case 'Regular': {
			return 'blue'
		}
		case 'Senior': {
			return 'green'
		}
		default: {
			return 'gray'
		}
	}
}

interface CandidateCardProps {
	logo: string
	name: string
	positionName: string
	seniority: Seniority
	availability: string
	salaryMin: number
	salaryMax: number
	currency: 'PLN' | 'EUR' | 'USD'
	technologyStack: string[]
}

const CandidateCard: FC<CandidateCardProps> = ({
	logo,
	name,
	positionName,
	seniority,
	availability,
	salaryMin,
	salaryMax,
	currency,
	technologyStack,
}) => (
	<div className="max-w-[417px] rounded-xl shadow-container">
		<div className="p-4 space-y-4 md:p-6 md:pb-4">
			{/* Header */}
			<div className="flex gap-5">
				<img
					src={logo}
					alt="logo"
					className="object-cover object-center rounded-full max-w-[50px] max-h-[50px] aspect-square"
					width={50}
					height={50}
				/>
				<div>
					<div className="text-lg font-semibold text-brand-gray-400">{name}</div>
					<div className="text-xs font-medium text-brand-gray-200">{positionName}</div>
				</div>
			</div>
			{/* Middle */}
			<div className="flex flex-wrap gap-2 md:gap-4">
				<Tag color={getSeniorityLabelColor(seniority)} size="sm">
					{seniority}
				</Tag>
				<Tag color="white" size="sm">
					<span className="flex items-center justify-center gap-1 md:gap-2">
						<SalaryIcon />
						{salaryMin.toLocaleString()} - {salaryMax.toLocaleString()} {currency}
					</span>
				</Tag>
				<Tag color="white" size="sm">
					<span className="flex items-center gap-1 md:gap-2">
						<AvailabilityIcon />
						<span className="text-[11px] md:text-xs font-medium text-brand-gray-300">{availability}</span>
					</span>
				</Tag>
			</div>
		</div>
		{/* Footer */}
		<div className="p-4 space-y-2 bg-brand-gray-100 md:p-6 md:pt-4 rounded-b-xl">
			<div className="font-bold text-[10px] tracking-widest uppercase text-brand-gray-200">Technology stack</div>
			<div className="flex flex-wrap gap-1 md:gap-2">
				{technologyStack
					.filter((_, index) => index < 4)
					.map(technology => (
						<Tag key={technology} size="sm" color="white">
							{technology}
						</Tag>
					))}

				{technologyStack.length >= 5 ? (
					<div className="hidden sm:block">
						<Tag color="white" size="sm">
							<MoreIcon />
						</Tag>
					</div>
				) : null}
			</div>
		</div>
	</div>
)

CandidateCard.displayName = 'CandidateCard'

export default CandidateCard
